import {
  Checkbox,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@ibsys/kit';
import Colorful from '@uiw/react-color-colorful';
import { hexToHsva, hslaToHsva, hsvaToHexa, hsvaToHsla } from '@uiw/color-convert';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isFullyTransparent, isValidHex, normalizeHex } from '../lib/colorUtils';
import { HexColor, HsvaColor } from '@uiw/react-color';

const TRANSPARENT_HEX = '#00000000';
const FALLBACK_HEX = '#000000';

type EditMode = 'hex' | 'hsl';

type ColorPickerPopoverProps = {
  color: string;
  onColorChange: (hex: HexColor) => void;
  disabled?: boolean;
  children: React.ReactNode;
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export const ColorPickerPopover: FC<ColorPickerPopoverProps> = ({
  color,
  onColorChange,
  disabled = false,
  children,
}) => {
  const { t } = useTranslation(['themes']);
  const [isOpen, setIsOpen] = useState(false);
  const [editMode, setEditMode] = useState<EditMode>('hex');
  const isTransparent = isFullyTransparent(color);
  const effectiveHex = isTransparent ? FALLBACK_HEX : color;

  const parseColorToHsva = useCallback((hex: string) => {
    const hsva = hexToHsva(hex);
    return { ...hsva, a: hsva.a ?? 1 };
  }, []);

  const hsvaToHexaString = useCallback((h: HsvaColor): HexColor => {
    const a = h.a ?? 1;
    return (a < 1 ? hsvaToHexa(h).toUpperCase() : hsvaToHexa(h).toUpperCase()) as HexColor;
  }, []);

  const [hsva, setHsva] = useState(() => parseColorToHsva(effectiveHex));
  const [hexInput, setHexInput] = useState(effectiveHex);

  const hsla = useMemo(() => hsvaToHsla(hsva), [hsva]);
  const [hslInputs, setHslInputs] = useState({
    h: String(Math.round(hsla.h)),
    s: String(Math.round(hsla.s)),
    l: String(Math.round(hsla.l)),
    a: String((hsva.a ?? 1).toFixed(2)),
  });

  useEffect(() => {
    setHslInputs(() => ({
      h: String(Math.round(hsla.h)),
      s: String(Math.round(hsla.s)),
      l: String(Math.round(hsla.l)),
      a: String((hsva.a ?? 1).toFixed(2)),
    }));
  }, [hsla.h, hsla.s, hsla.l, hsva.a]);

  useEffect(() => {
    if (isOpen) {
      const hex = isFullyTransparent(color) ? FALLBACK_HEX : color;
      const parsed = parseColorToHsva(hex);
      setHsva(parsed);
      setHexInput(hsvaToHexaString(parsed));
    }
  }, [color, isOpen, parseColorToHsva, hsvaToHexaString]);

  const handleTransparentChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        onColorChange(TRANSPARENT_HEX);
      } else {
        const hex: HexColor = isValidHex(hexInput) ? normalizeHex(hexInput) : hsvaToHexaString(hsva);
        onColorChange(hex);
      }
    },
    [hexInput, hsva, onColorChange, hsvaToHexaString],
  );

  const handleHexInputChange = useCallback(
    (value: string) => {
      setHexInput(value);
      if (isValidHex(value)) {
        const normalized = normalizeHex(value);
        setHsva(parseColorToHsva(normalized));
      }
    },
    [parseColorToHsva],
  );

  const handleHslInputChange = useCallback(
    (field: 'h' | 's' | 'l' | 'a', value: string) => {
      const num = value === '' ? NaN : Number(value);
      setHslInputs((prev) => ({ ...prev, [field]: value }));
      if (!Number.isNaN(num)) {
        const h = field === 'h' ? clamp(num, 0, 360) : hsla.h;
        const s = field === 's' ? clamp(num, 0, 100) : hsla.s;
        const l = field === 'l' ? clamp(num, 0, 100) : hsla.l;
        const a = field === 'a' ? clamp(num, 0, 1) : (hsva.a ?? 1);
        const newHsva = hslaToHsva({ h, s, l, a });
        setHsva(newHsva);
        setHexInput(hsvaToHexaString(newHsva));
      }
    },
    [hsla.h, hsla.s, hsla.l, hsva.a, hsvaToHexaString],
  );

  const handleColorfulChange = useCallback(
    (newColor: { hsva: typeof hsva }) => {
      setHsva(newColor.hsva);
      setHexInput(hsvaToHexaString(newColor.hsva));
    },
    [hsvaToHexaString],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && isOpen && !isFullyTransparent(color)) {
        const finalHex = isValidHex(hexInput) ? normalizeHex(hexInput) : hsvaToHexaString(hsva);
        onColorChange(finalHex);
      }
      setIsOpen(open);
    },
    [isOpen, hexInput, hsva, onColorChange, color, hsvaToHexaString],
  );

  if (disabled) {
    return <>{children}</>;
  }

  const showPicker = !isTransparent;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="flex flex-col gap-4">
          <Checkbox
            value={isTransparent}
            onClick={() => handleTransparentChange(!isTransparent)}
            label={t('colorPicker.transparent')}
          />
          {showPicker && (
            <>
              <Colorful color={hsva} onChange={handleColorfulChange} />
              <Tabs value={editMode} onValueChange={(v) => setEditMode(v as EditMode)} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="hex">{t('colorPicker.editModeHex')}</TabsTrigger>
                  <TabsTrigger value="hsl">{t('colorPicker.editModeHsl')}</TabsTrigger>
                </TabsList>
                <TabsContent value="hex" className="mt-2">
                  <Input
                    label={t('colorHex')}
                    value={hexInput}
                    onChange={(e) => handleHexInputChange(e.target.value)}
                    placeholder="#000000"
                    className="font-mono"
                  />
                </TabsContent>
                <TabsContent value="hsl" className="mt-2 flex flex-col gap-2">
                  <Input
                    label={t('colorPicker.hue')}
                    type="number"
                    min={0}
                    max={360}
                    value={hslInputs.h}
                    onChange={(e) => handleHslInputChange('h', e.target.value)}
                  />
                  <Input
                    label={t('colorPicker.saturation')}
                    type="number"
                    min={0}
                    max={100}
                    value={hslInputs.s}
                    onChange={(e) => handleHslInputChange('s', e.target.value)}
                  />
                  <Input
                    label={t('colorPicker.lightness')}
                    type="number"
                    min={0}
                    max={100}
                    value={hslInputs.l}
                    onChange={(e) => handleHslInputChange('l', e.target.value)}
                  />
                  <Input
                    label={t('colorPicker.alpha')}
                    type="number"
                    min={0}
                    max={1}
                    step={0.01}
                    value={hslInputs.a}
                    onChange={(e) => handleHslInputChange('a', e.target.value)}
                  />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
